import { DataLoadError, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../Base.constants';
import useFetchSnippet from '../../hooks/snippets/useFetchSnippet';
import { Snippet, SnippetType } from '../../models';
import { isNotFound } from '../../utils/navigationUtils';
import { isAnalystTag } from '../../utils/tagUtils';

import styles from './CustomPage.module.scss';
import CustomPageAuthors from './CustomPageAuthors';

const cx = classNames.bind(styles);

const CustomPage = (): React.ReactElement => {
  const { key } = useParams<string>();
  const navigate = useNavigate();

  const {
    data: snippet,
    isLoading,
    isRefetching,
    isError,
    refetch,
    error,
  } = useFetchSnippet(SnippetType.CUSTOM_PAGE, key!);

  useEffect(() => {
    if (!isLoading && isNotFound(error) && !snippet) {
      navigate(ROUTES.notFound, { replace: true });
    }
  }, [snippet, isLoading, error]);

  const renderPage = (snippet?: Snippet) => {
    const analystTags = snippet?.tagIds.filter(isAnalystTag);
    const showAuthorsBox = snippet?.displayAuthorsBox && analystTags?.length;

    return (
      <>
        <h1 className={cx('title')} data-testid="custom-page-title">
          {snippet?.title}
        </h1>
        <div className={cx('content')}>
          <div
            className={cx('main')}
            dangerouslySetInnerHTML={{ __html: snippet?.body || '' }}
            data-testid="custom-page-body"
          />
          <div className={cx('side')}>{showAuthorsBox && <CustomPageAuthors tagIds={analystTags} />}</div>
        </div>
      </>
    );
  };

  if (isError && !isNotFound(error)) {
    return (
      <div className={cx('body')}>
        <div className={cx('error-container')}>
          <DataLoadError onRetryClick={refetch} />
        </div>
      </div>
    );
  }

  return (
    <div className={cx('body')}>
      {isLoading || isRefetching ? (
        <div className={cx('loader-container')}>
          <Loader position="center" />
        </div>
      ) : (
        renderPage(snippet)
      )}
    </div>
  );
};

export default CustomPage;
